import gsap from "gsap";
import ScrollToPlugin from "gsap/ScrollToPlugin"
gsap.registerPlugin(ScrollToPlugin);

export default class PageSearch {
  constructor(params) {
    this.target = params.target;
    this.targets = document.querySelectorAll(params.target);
    this.containerClass = params.containerClass;
    this.grandContainerClass = params.grandContainerClass;
    this.trigger = document.querySelector(params.trigger);
    this.marker = params.marker;
    this.narrow = params.narrow;
    this.count = params.count;
    this.control = params.control;
    this.searchBar = this.createSearchBar();
    this.input = this.searchBar.querySelector("input");
    this.matches = [];
    this.originalTexts = new Map();
    this.currentIndex = -1;
    this.initEvents();
    this.checkURLForSearch();
  }

  createSearchBar() {
    const div = document.createElement("div");
    let countHTML = '';
    if (this.count) {
      countHTML = `<div class="search-bar__count" id="search-count">${this.narrow ? '0' : '0/0'}</div>`;
    }
    let controlHTML = '';
    if (this.control) {
      controlHTML = `<div class="search-bar-controls">
            <button class="search-bar-btn" id="search-prev-btn">↑</button>
            <button class="search-bar-btn" id="search-next-btn">↓</button>
          </div>`;
    }

    div.innerHTML = `
      <div class="search-bar" id="search-bar">
        <div class="search-bar-inner">
          <div class="search-bar__input">
            <input type="text" placeholder="キーワードで探す">
            ${countHTML}
          </div>
          ${controlHTML}
          <div class="search-bar-close">
            <button class="search-bar-close-btn" id="search-close-btn"></button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(div);
    return div.querySelector("#search-bar");
  }

  initEvents() {
    if (!this.targets.length || !this.trigger) return false;

    this.trigger.addEventListener('click', () => {
      this.showSearchBar();
    })

    this.searchBar.querySelector("#search-close-btn").addEventListener("click", () => this.closeSearchBar());

    if (this.control) {
      this.searchBar.querySelector("#search-prev-btn").addEventListener("click", () => this.navigate(-1));
      this.searchBar.querySelector("#search-next-btn").addEventListener("click", () => this.navigate(1));
    }

    this.input.addEventListener("input", () => this.findMatches());
    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.navigate(e.shiftKey ? -1 : 1);
      }
    });
  }

  checkURLForSearch() {
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get("s");
    if (searchQuery) {
      this.input.value = decodeURIComponent(searchQuery);
      this.showSearchBar();
      this.findMatches();
    }
  }

  showSearchBar() {
    this.searchBar.classList.add('is-open');
    this.input.focus();
    this.findMatches();
  }

  closeSearchBar() {
    this.searchBar.classList.remove('is-open');
    this.input.value = "";
    this.clearHighlights();
    this.showContents();
    this.resetCount();
    this.matches = [];
  }

  findMatches() {
    this.clearHighlights();
    this.showContents();
    this.resetCount();
    this.matches = [];
    const query = this.input.value.trim();
    if (!query) return;

    this.targets.forEach((el) => {
      const textContent = el.innerText;
      if (textContent.includes(query)) {
        this.matches.push(el);
        if (!this.originalTexts.has(el)) {
          this.originalTexts.set(el, el.innerHTML);
        }
      }
    });

    this.highlightMatches(query);
    this.currentIndex = -1;
    this.navigate(1);
  }

  highlightMatches(query) {
    if (!query || !this.marker) return;
    const regex = new RegExp(`(${query})`, "gi");

    this.matches.forEach((el) => {
      if (!this.originalTexts.has(el)) {
        this.originalTexts.set(el, el.innerHTML);
      }

      this.highlightTextNodes(el, regex);
    });
  }

  highlightTextNodes(element, regex) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    textNodes.forEach((node) => {
      const parent = node.parentNode;
      if (parent.tagName === "MARK") return;

      const fragment = document.createDocumentFragment();
      const parts = node.nodeValue.split(regex);

      parts.forEach((part, index) => {
        if (index % 2 === 0) {
          fragment.appendChild(document.createTextNode(part));
        } else {
          const mark = document.createElement("mark");
          mark.className = "highlight-marker";
          mark.textContent = part;
          fragment.appendChild(mark);
        }
      });

      parent.replaceChild(fragment, node);
    });
  }

  clearHighlights() {
    if (!this.marker) return;
    this.originalTexts.forEach((html, el) => {
      el.innerHTML = html;
    });
    this.originalTexts.clear();
  }

  showContents() {
    if (!this.narrow) return;
    if (this.grandContainerClass) {
      document.querySelectorAll(this.grandContainerClass).forEach(container => {
        container.style.display = 'block';
      })
    }

    this.targets.forEach((el) => {
      if (this.containerClass) {
        el.closest(this.containerClass).style.display = 'block';
      } else {
        el.style.display = 'block';
      }
    })
  }

  hideContents() {
    if (!this.narrow) return;
    this.targets.forEach((el) => {
      if (this.containerClass) {
        el.closest(this.containerClass).style.display = 'none';
      } else {
        el.style.display = 'none';
      }
      if (this.matches.includes(el)) {
        if (this.containerClass) {
          el.closest(this.containerClass).style.display = 'block';
        } else {
          el.style.display = 'block';
        }
      }
    })

    if (this.grandContainerClass) {
      document.querySelectorAll(this.grandContainerClass).forEach(container => {
        let showCount = 0,
          targets = null;

        if (this.containerClass) {
          targets = container.querySelectorAll(this.containerClass);
        } else {
          targets = container.querySelectorAll(this.target);
        }

        targets.forEach(target => {
          if (target.style.display === 'block') {
            showCount++;
          }
        })

        if (showCount === 0) {
          container.style.display = 'none';
        }
      })
    }
  }

  setCount(current, total) {
    if (!this.count) return;
    this.searchBar.querySelector("#search-count").textContent = this.narrow ? `${total}` : `${current}/${total}`;
  }

  resetCount() {
    if (!this.count) return;
    this.searchBar.querySelector("#search-count").textContent = this.narrow ? '0' : '0/0';
  }

  navigate(direction) {
    if (this.matches.length === 0) return;
    this.currentIndex = (this.currentIndex + direction + this.matches.length) % this.matches.length;
    const target = this.matches[this.currentIndex];

    this.setCount(this.currentIndex + 1, this.matches.length)

    if (this.narrow) {
      this.hideContents();
    } else {
      gsap.to(window, {
        ease: "power2.Out",
        duration: .2,
        scrollTo: {
          y: target,
          offsetY: document.querySelector('#site-header').clientHeight + document.querySelector('#search-bar').clientHeight
        }
      });
    }
    // setTimeout(() => this.input.focus(), 10);
  }
}